// Structural subset of HsTransportResponse; src/shared imports nothing else from the SDK.
interface ApiResponse {
  status: number;
  statusText: string;
  json<T>(): Promise<T>;
}

interface ApiErrorBody {
  message?: string;
  Message?: string;
  type?: string;
  Type?: string;
}

/**
 * A non-success API response. `message` is the API's own error text when the body carries one.
 * API addons bundle their own copy of this class, so test errors with `hsIsApiError`, not `instanceof`.
 */
export class HsApiError extends Error {
  public constructor(message: string, public readonly status: number) {
    super(message);
    this.name = 'HsApiError';
  }
}

export function hsIsApiError(error: unknown): error is HsApiError {
  return error instanceof Error && error.name === 'HsApiError' && typeof (error as HsApiError).status === 'number';
}

export async function hsToApiError(response: ApiResponse): Promise<HsApiError> {
  const statusText: string = response.statusText.trim().length > 0 ? response.statusText : 'Request failed';
  const fallback: string = `HTTP ${response.status}: ${statusText}`;
  try {
    const body: ApiErrorBody = await response.json<ApiErrorBody>();
    return new HsApiError(body.message ?? body.Message ?? body.type ?? body.Type ?? fallback, response.status);
  } catch {
    return new HsApiError(fallback, response.status);
  }
}

/** Throws `HsApiError` unless the status is 2xx. */
export async function hsEnsureOk<T extends ApiResponse>(response: T): Promise<T> {
  if (response.status < 200 || response.status > 299) {
    throw await hsToApiError(response);
  }
  return response;
}
